<template>
  <main class="system-models-page">
    <header>
      <div>
        <h1>系统模型</h1>
        <p>密钥仅以加密形式保存；用户调用系统模型时按输入和输出 Token 消耗 Credits。</p>
      </div>
      <a-space>
        <a-button @click="load"><i class="ri-refresh-line" />刷新</a-button>
        <a-button type="primary" @click="openCreate"
          ><i class="ri-add-line" />新增系统模型</a-button
        >
      </a-space>
    </header>

    <a-alert
      type="info"
      show-icon
      message="计费单位：Credits / 百万 Token"
      description="API Key 以加密形式保存。更新已有模型时会显示当前密钥，可按需替换。"
    />

    <a-card class="model-list-card" :loading="loading">
      <template #title
        ><span><i class="ri-brain-line" />已配置模型</span></template
      >
      <template #extra
        ><a-tag>{{ models.length }} 个</a-tag></template
      >
      <a-empty v-if="!loading && !models.length" description="尚未配置系统模型">
        <a-button type="primary" @click="openCreate"
          ><i class="ri-add-line" />新增系统模型</a-button
        >
      </a-empty>
      <a-list v-else class="model-list" :data-source="models">
        <template #renderItem="{ item }">
          <a-list-item class="model-item">
            <a-list-item-meta>
              <template #avatar
                ><span class="model-icon"><i class="ri-cpu-line" /></span
              ></template>
              <template #title
                >{{ item.name }} <a-tag v-if="item.isDefault" color="processing">默认</a-tag>
                <a-tag v-if="item.supportsThinkingEffort" color="purple">强度可调</a-tag>
                <a-tag v-if="item.supportsMultimodal" color="cyan">多模态</a-tag>
                <a-tag v-if="item.apiKeyDecryptionFailed" color="error">需重填 API Key</a-tag>
                <a-tag v-if="!item.enabled">已停用</a-tag></template
              >
              <template #description
                ><span class="model-id">{{ item.model }}</span
                ><span class="model-detail"
                  >上下文 {{ item.contextWindowTokens ?? '平台默认' }} · 输入
                  {{ item.inputCreditsPerMillion }} · 输出
                  {{ item.outputCreditsPerMillion }} Credits / 百万 Token</span
                ></template
              >
            </a-list-item-meta>
            <a-space wrap>
              <a-button
                type="link"
                :loading="testingTarget === item._id"
                :disabled="!item.apiKey || testingTarget !== null || saving"
                title="发送简短测试请求，可能产生供应商费用"
                @click="testConnection(item, item._id)"
                ><i class="ri-plug-line" aria-hidden="true" />测试连接</a-button
              >
              <a-button type="link" @click="openEdit(item)"
                ><i class="ri-edit-line" />编辑</a-button
              >
              <a-popconfirm
                v-if="item.enabled"
                title="确定禁用此系统模型吗？"
                description="禁用后不能再被选择，正在使用它的 Agent 也将无法继续调用。"
                ok-text="禁用"
                cancel-text="取消"
                @confirm="disable(item)"
              >
                <a-button type="link" danger :loading="operatingId === item._id"
                  ><i class="ri-forbid-2-line" />禁用</a-button
                >
              </a-popconfirm>
              <a-popconfirm
                title="确定删除此系统模型吗？"
                description="删除会清除使用此模型的账户和 Agent 偏好，后续调用将回退到其他可用模型。"
                ok-text="删除"
                :ok-button-props="{ danger: true }"
                cancel-text="取消"
                @confirm="remove(item)"
              >
                <a-button type="link" danger :loading="operatingId === item._id"
                  ><i class="ri-delete-bin-line" />删除</a-button
                >
              </a-popconfirm>
            </a-space>
          </a-list-item>
        </template>
      </a-list>
    </a-card>

    <a-modal
      v-model:open="editorOpen"
      :title="editingId ? '编辑系统模型' : '新增系统模型'"
      width="680px"
      :closable="!saving && testingTarget !== 'editor'"
      :mask-closable="!saving && testingTarget !== 'editor'"
      :keyboard="!saving && testingTarget !== 'editor'"
      :footer="null"
      @cancel="closeEditor"
    >
      <a-form :model="form" layout="vertical" @finish="save" @finish-failed="onFinishFailed">
        <a-form-item
          name="name"
          label="名称"
          :rules="[{ required: true, whitespace: true, message: '请输入模型名称' }]"
        >
          <a-input v-model:value="form.name" :maxlength="80" placeholder="例如：DeepSeek Chat" />
        </a-form-item>
        <a-form-item label="Provider 请求策略" name="providerKind">
          <a-select v-model:value="form.providerKind" :options="providerOptions" />
          <small v-if="form.providerKind === 'claude'"
            >支持 Claude 官方或原生中转，Base URL 可填写 https://api.anthropic.com 或带 /v1
            的地址。</small
          >
        </a-form-item>
        <a-form-item
          name="baseUrl"
          label="Base URL"
          :rules="[
            { required: true, whitespace: true, message: '请输入服务地址' },
            { type: 'url', message: '请输入有效的 URL' },
          ]"
        >
          <a-input v-model:value="form.baseUrl" placeholder="https://api.openai.com/v1" />
        </a-form-item>
        <a-form-item
          name="apiKey"
          label="API Key"
          :rules="[{ required: true, whitespace: true, message: '请输入 API Key' }]"
        >
          <a-input-password v-model:value="form.apiKey" placeholder="请输入 API Key" />
        </a-form-item>
        <a-form-item
          name="model"
          :rules="[{ required: true, whitespace: true, message: '请选择或输入模型 ID' }]"
        >
          <template #label
            ><span class="model-id-label"
              >模型 ID
              <a-button type="link" size="small" @click="toggleModelInputMode"
                ><i :class="modelInputMode === 'select' ? 'ri-pencil-line' : 'ri-list-check-2'" />{{
                  modelInputMode === 'select' ? '手动输入' : '从列表选择'
                }}</a-button
              ></span
            ></template
          >
          <div class="model-id-control">
            <a-select
              v-if="modelInputMode === 'select'"
              v-model:value="form.model"
              :options="modelOptions"
              show-search
              :filter-option="filterModel"
              placeholder="获取列表后选择模型"
              not-found-content="没有匹配模型"
            />
            <a-input v-else v-model:value="form.model" placeholder="请输入模型 ID" />
            <a-button
              :loading="discovering"
              :disabled="!form.baseUrl || !form.apiKey"
              @click="discover"
              ><i class="ri-refresh-line" />获取模型列表</a-button
            >
          </div>
        </a-form-item>
        <a-form-item label="上下文窗口 Token">
          <a-input-number
            v-model:value="form.contextWindowTokens"
            :min="1024"
            :max="2000000"
            placeholder="留空时使用平台默认输入预算"
          />
        </a-form-item>
        <div class="rates">
          <a-form-item
            name="inputCreditsPerMillion"
            label="输入积分单价（Credits / 百万 Token）"
            :rules="[
              { required: true, type: 'number', min: 0, message: '请输入不小于 0 的积分单价' },
            ]"
          >
            <a-input-number
              v-model:value="form.inputCreditsPerMillion"
              :min="0"
              :precision="3"
              :step="0.001"
            />
          </a-form-item>
          <a-form-item
            name="outputCreditsPerMillion"
            label="输出积分单价（Credits / 百万 Token）"
            :rules="[
              { required: true, type: 'number', min: 0, message: '请输入不小于 0 的积分单价' },
            ]"
          >
            <a-input-number
              v-model:value="form.outputCreditsPerMillion"
              :min="0"
              :precision="3"
              :step="0.001"
            />
          </a-form-item>
        </div>
        <p class="rate-hint">
          这里填写平台向用户收取的积分价格，不是供应商 API
          的货币成本。输入和输出可以分别定价；两项均为 0 时，该模型免费。
        </p>
        <div class="model-switches">
          <label><a-switch v-model:checked="form.enabled" /><span>启用</span></label>
          <label><a-switch v-model:checked="form.isDefault" /><span>设为默认</span></label>
          <label><a-switch v-model:checked="form.thinkingEnabled" /><span>开启思考</span></label>
          <label
            ><a-switch
              v-model:checked="form.supportsThinkingEffort"
              :disabled="!form.thinkingEnabled"
            /><span>支持调节思考强度</span></label
          >
          <label
            ><a-switch v-model:checked="form.supportsMultimodal" /><span>支持多模态</span></label
          >
        </div>
        <p class="model-test-hint">
          测试连接会使用当前填写的配置发送简短请求，无需先保存，可能产生供应商费用。
        </p>
        <footer class="model-editor-actions">
          <a-button
            :loading="testingTarget === 'editor'"
            :disabled="
              saving ||
              testingTarget !== null ||
              !form.baseUrl.trim() ||
              !form.apiKey.trim() ||
              !form.model.trim()
            "
            @click="testConnection()"
            ><i class="ri-plug-line" aria-hidden="true" />测试连接</a-button
          >
          <a-button :disabled="saving || testingTarget === 'editor'" @click="closeEditor"
            >取消</a-button
          >
          <a-button
            type="primary"
            html-type="submit"
            :loading="saving"
            :disabled="testingTarget !== null"
            ><i :class="editingId ? 'ri-save-line' : 'ri-add-line'" />{{
              editingId ? '保存模型' : '新增模型'
            }}</a-button
          >
        </footer>
      </a-form>
    </a-modal>
  </main>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { message } from 'ant-design-vue';
import type {
  ModelProviderKind,
  ModelConnectionTestInput,
  ModelConnectionTestResult,
} from 'shared';
import { MODEL_PROVIDER_OPTIONS } from 'shared';
import { adminApi } from '@/api';

type SystemModel = {
  _id: string;
  name: string;
  baseUrl: string;
  model: string;
  providerKind: ModelProviderKind;
  apiKey: string;
  apiKeyDecryptionFailed: boolean;
  enabled: boolean;
  isDefault: boolean;
  hasApiKey: boolean;
  thinkingEnabled: boolean;
  supportsThinkingEffort: boolean;
  supportsMultimodal: boolean;
  contextWindowTokens?: number;
  inputCreditsPerMillion: number;
  outputCreditsPerMillion: number;
};
const models = ref<SystemModel[]>([]);
const loading = ref(false);
const saving = ref(false);
const testingTarget = ref<string | null>(null);
const discovering = ref(false);
const operatingId = ref<string | null>(null);
const discoveredModels = ref<string[]>([]);
const editingId = ref<string | null>(null);
const editorOpen = ref(false);
const modelInputMode = ref<'select' | 'manual'>('select');
const providerOptions = MODEL_PROVIDER_OPTIONS;
const defaults = () => ({
  name: '',
  baseUrl: '',
  apiKey: '',
  model: '',
  providerKind: 'openai-compatible' as ModelProviderKind,
  enabled: true,
  isDefault: false,
  thinkingEnabled: true,
  supportsThinkingEffort: false,
  supportsMultimodal: false,
  contextWindowTokens: undefined as number | undefined,
  inputCreditsPerMillion: 0,
  outputCreditsPerMillion: 0,
});
const form = reactive(defaults());
const modelOptions = computed(() =>
  discoveredModels.value.map((model) => ({ label: model, value: model })),
);

async function load(): Promise<void> {
  loading.value = true;
  try {
    models.value = await adminApi.get<SystemModel[]>('/admin/system-models');
  } catch {
    message.error('系统模型加载失败');
  } finally {
    loading.value = false;
  }
}
async function discover(): Promise<void> {
  discovering.value = true;
  try {
    discoveredModels.value = await adminApi.post<string[]>('/admin/system-models/discover-models', {
      baseUrl: form.baseUrl,
      apiKey: form.apiKey,
      providerKind: form.providerKind,
    });
    if (!discoveredModels.value.length)
      message.warning('服务未返回可用模型，你仍可手动填写模型 ID。');
    else {
      modelInputMode.value = 'select';
      message.success(`已获取 ${discoveredModels.value.length} 个模型。`);
    }
  } catch {
    message.error('模型列表获取失败，请检查服务地址和 API Key。');
  } finally {
    discovering.value = false;
  }
}
function openCreate(): void {
  reset();
  editorOpen.value = true;
}
function openEdit(item: SystemModel): void {
  editingId.value = item._id;
  Object.assign(form, { ...defaults(), ...item });
  discoveredModels.value = [item.model];
  modelInputMode.value = 'select';
  editorOpen.value = true;
}
function toggleModelInputMode(): void {
  modelInputMode.value = modelInputMode.value === 'select' ? 'manual' : 'select';
}
function closeEditor(): void {
  if (saving.value || testingTarget.value === 'editor') return;
  editorOpen.value = false;
  reset();
}
function reset(): void {
  editingId.value = null;
  discoveredModels.value = [];
  modelInputMode.value = 'select';
  Object.assign(form, defaults());
}
function filterModel(input: string, option?: { label?: string; value?: string | number }): boolean {
  return `${option?.label ?? ''} ${option?.value ?? ''}`
    .toLowerCase()
    .includes(input.toLowerCase());
}
async function save(): Promise<void> {
  if (testingTarget.value !== null || saving.value) return;
  saving.value = true;
  try {
    const body = { ...form };
    if (editingId.value) await adminApi.patch(`/admin/system-models/${editingId.value}`, body);
    else await adminApi.post('/admin/system-models', body);
    message.success('系统模型已保存');
    editorOpen.value = false;
    reset();
    await load();
  } catch {
    message.error('保存失败，请检查模型配置和权限');
  } finally {
    saving.value = false;
  }
}
async function testConnection(
  input: ModelConnectionTestInput = form,
  target = 'editor',
): Promise<void> {
  if (testingTarget.value !== null || saving.value) return;
  testingTarget.value = target;
  try {
    const result = await adminApi.post<ModelConnectionTestResult>(
      '/admin/system-models/test-connection',
      {
        baseUrl: input.baseUrl,
        apiKey: input.apiKey,
        model: input.model,
        providerKind: input.providerKind,
        thinkingEnabled: input.thinkingEnabled,
      },
    );
    message.success(`测试成功：${result.model}，耗时 ${result.latencyMs} 毫秒。`);
  } catch (error) {
    message.error(`测试失败：${error instanceof Error ? error.message : '请检查模型配置后重试'}`);
  } finally {
    testingTarget.value = null;
  }
}
async function disable(item: SystemModel): Promise<void> {
  operatingId.value = item._id;
  try {
    await adminApi.patch(`/admin/system-models/${item._id}/disable`, {});
    message.success('系统模型已禁用');
    await load();
  } catch {
    message.error('禁用失败，请先切换默认系统模型或检查权限');
  } finally {
    operatingId.value = null;
  }
}
async function remove(item: SystemModel): Promise<void> {
  operatingId.value = item._id;
  try {
    await adminApi.delete(`/admin/system-models/${item._id}`);
    message.success('系统模型已删除');
    await load();
  } catch {
    message.error('删除失败，请先切换默认系统模型或检查权限');
  } finally {
    operatingId.value = null;
  }
}
function onFinishFailed(): void {
  message.warning('请先修正表单中的错误项。');
}
onMounted(() => {
  void load();
});
</script>

<style scoped>
.system-models-page {
  max-width: 1080px;
  margin: 0 auto;
  padding: 32px;
}
.system-models-page > header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}
.system-models-page h1 {
  margin: 0 0 7px;
  color: var(--aw-text);
}
.system-models-page header p {
  max-width: 680px;
  margin: 0;
  color: var(--aw-text-secondary);
}
.model-list-card {
  margin-top: 16px;
}
.model-list-card :deep(.ant-card-head-title) {
  display: flex;
  align-items: center;
  gap: 8px;
}
.model-list-card :deep(.ant-card-head-title i) {
  color: var(--aw-primary);
  font-size: 18px;
}
.model-item {
  padding: 16px 4px;
}
.model-item:last-child {
  border-bottom: 0;
}
.model-icon {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border-radius: var(--aw-radius-sm);
  color: var(--aw-primary);
  background: var(--aw-primary-soft);
  font-size: 18px;
}
.model-item :deep(.ant-list-item-meta-title) {
  margin-bottom: 2px;
  color: var(--aw-text);
}
.model-id,
.model-detail {
  display: block;
}
.model-id {
  color: var(--aw-text-secondary);
  font-size: 12px;
}
.model-detail {
  margin-top: 2px;
  overflow: hidden;
  color: var(--aw-text-tertiary);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.model-id-control {
  display: flex;
  gap: 8px;
}
.model-id-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.model-id-label :deep(.ant-btn) {
  height: auto;
  padding: 0;
}
.model-id-control :deep(.ant-select),
.model-id-control :deep(.ant-input) {
  flex: 1;
  min-width: 0;
}
.rates {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.rates :deep(.ant-input-number) {
  width: 100%;
}
.rate-hint {
  margin: -6px 0 18px;
  color: var(--aw-text-tertiary);
  font-size: 12px;
  line-height: 1.6;
}
.model-test-hint {
  margin: 16px 0 0;
  color: var(--aw-text-secondary);
  font-size: 12px;
}
.model-switches {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
}
.model-switches label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--aw-text-secondary);
  cursor: pointer;
  user-select: none;
}
.model-editor-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}
@media (max-width: 760px) {
  .system-models-page {
    padding: 20px 14px;
  }
  .system-models-page > header {
    display: grid;
  }
  .system-models-page > header :deep(.ant-space) {
    justify-self: start;
  }
  .model-item {
    align-items: flex-start;
  }
  .model-item :deep(.ant-list-item-action) {
    margin-inline-start: 12px;
  }
  .model-id-control,
  .rates {
    grid-template-columns: 1fr;
  }
  .model-id-control {
    display: grid;
  }
}
</style>
